import React from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Table,
  Space,
  Button,
  Popover,
  Dropdown,
  Menu,
  Tag,
  Modal,
  Form,
  Select,
  Input,
  message,
} from "antd";
import {
  SettingOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { ListComponent, BaseListComponentProps } from "../BaseComponent";
import {
  SearchRequest,
  AdoptionApplicationReturnRequest,
  AdoptionApplicationForceSetAdoptedRequest,
} from "../../services/api/interfaces/ApiRequest";
import { IntegrationContextData } from "../../context/IntegrationContext";
import { IntegrationPartnerContextData } from "../../context/IntegrationPartnerContext";
import SetForceAdopted from "./SetForceAdopted";
import { Pagination } from "components/Pagination";

type AdoptionApplicationListProps = BaseListComponentProps & {
  loading: boolean;
  applications: any;
  pagination: any;

  history: any;
  searchOptions: any;

  integrationContext: IntegrationContextData;
  integrationPartnerContext: IntegrationPartnerContextData;

  loadAdoptionApplications: (args: SearchRequest) => void;
  adoptionApplicationReturn: (
    args: AdoptionApplicationReturnRequest
  ) => Promise<any>;
  adoptionApplicationForceSetAdopted: (
    args: AdoptionApplicationForceSetAdoptedRequest
  ) => Promise<any>;
};

export default class AdoptionApplicationList extends ListComponent<AdoptionApplicationListProps> {
  static defaultProps = {
    loading: false,

    loadAdoptionApplications: () => {},
    adoptionApplicationReturn: () => {},
    adoptionApplicationForceSetAdopted: () => {},
  };

  ACCEPTABLE_FORCE_ADOPTED: Readonly<string[]> = [
    "UNDER_REVIEW",
    "APPROVED",
    "PAID",
    "SIGNED",
    "SIGNED",
    "ADOPTED",
    "PENDING",
  ];

  adoptionReturnForm: any = null;

  tableColumns = [
    {
      title: "UUID",
      dataIndex: "uuid",
      key: "uuid",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.uuid - b.uuid,
    },
    {
      title: "Adopter",
      dataIndex: ["adopter_user", "first_name"],
      key: "adopter_user.first_name",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) =>
        a.adopter_user?.first_name - b.adopter_user?.first_name,
    },
    {
      title: "Pet",
      dataIndex: ["pet", "name"],
      key: "pet.name",
      sorter: (a: any, b: any) => a.pet?.name - b.pet?.name,
    },
    {
      title: "Location",
      dataIndex: ["pet", "branch", "name"],
      key: "pet.branch.name",
      sorter: (a: any, b: any) => a.pet?.branch?.name - b.pet?.branch?.name,
      render: (text: string, record: any) => {
        if (!record.pet?.branch?.shelter) {
          return `${record.pet?.branch?.name} / ${record.pet?.branch?.shelter?.name}`;
        }

        const popoverContent = (
          <Space direction="vertical">
            <div>
              <Typography.Text strong>Shelter: </Typography.Text>
              <Typography.Text>
                {record.pet?.branch?.shelter?.name}
              </Typography.Text>
            </div>
            <div>
              <Typography.Text strong>Location: </Typography.Text>
              <Typography.Text>{record.pet?.branch?.name}</Typography.Text>
            </div>
          </Space>
        );

        return (
          <Popover
            content={popoverContent}
            title="Location Info"
            trigger="hover"
          >
            <Link
              to={`/organization/shelter/${record.pet.branch.shelter.uuid}`}
            >
              {`${record.pet?.branch?.name} / ${record.pet?.branch?.shelter?.name}`}
            </Link>
          </Popover>
        );
      },
    },
    {
      title: "Returned",
      dataIndex: "adoption_return",
      key: "returned",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.adoption_return - b.adoption_return,
      render: (text: string, record: any) => {
        if (record.adoption_return !== null) {
          const popoverContent = (
            <Space direction="vertical">
              <div>
                <Typography.Text strong>Reason: </Typography.Text>
                <Typography.Text>
                  {record.adoption_return.reason_type_key}
                </Typography.Text>
              </div>
              <div>
                <Typography.Text strong>Description: </Typography.Text>
                <Typography.Text>
                  {record.adoption_return.description}
                </Typography.Text>
              </div>
            </Space>
          );

          return (
            <Popover
              content={popoverContent}
              title="Adoption Returned"
              trigger="hover"
            >
              <Tag color="volcano">Yes</Tag>
            </Popover>
          );
        } else {
          return <Tag color="geekblue">No</Tag>;
        }
      },
    },
    {
      title: "Canceled",
      dataIndex: "canceled",
      key: "canceled",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.canceled - b.canceled,
      render: (text: string, record: any) => {
        let color = "blue";
        if (record.canceled !== null) {
          color = "magenta";
        }

        return <Tag color={color}>{record.canceled ? "Yes" : "No"}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status_key",
      key: "status_key",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.status_key - b.status_key,
      render: (text: string, record: any) => {
        let color = "cyan";

        switch (text) {
          case "UNDER_REVIEW":
            color = "orange";
            break;
          case "APPROVED":
            color = "geekblue";
            break;
          case "DECLINED":
            color = "red";
            break;
          case "PAID":
            color = "gold";
            break;
          case "SIGNED":
            color = "purple";
            break;
          case "ADOPTED":
            color = "green";
            break;
          default:
            color = "cyan";
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "action",
      render: (text: string, record: any) => (
        <Space size="middle">
          {!record.adoption_return &&
          !record.canceled &&
          !record.withdrew &&
          (record.status_key === "ADOPTED" ||
            this.ACCEPTABLE_FORCE_ADOPTED.includes(record.status_key)) ? (
            <div ref={(refs) => this.actionRefs.edit.push(refs)}>
              <Dropdown
                overlay={this.renderActions(record)}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Button
                  type="dashed"
                  shape="round"
                  icon={<SettingOutlined />}
                />
              </Dropdown>
            </div>
          ) : null}
          {record.status_key === "APPROVED" ||
          record.status_key === "SIGNED" ? (
            <Link to={`adoption-application/${record.uuid}`}>
              <Button type="dashed" shape="round" icon={<EditOutlined />} />
            </Link>
          ) : null}
        </Space>
      ),
    },
  ];

  state = {
    currentShelterUuid: "",
    currentBranchUuid: "",
    currentApplicationUuid: "",
    currentApplicationStatus: "",
  };

  protected load() {
    this.searchOptions.search = {
      ...this.searchOptions.search,
      ...this.props.searchOptions.search,
    };

    this.props.loadAdoptionApplications(this.searchOptions);
  }

  handleAdoptionReturn = (uuid: string) => {
    const { adoptionApplicationReturn } = this.props;

    Modal.confirm({
      title: "Please, fill the fields below to proceed the adoption return",
      icon: <InfoCircleOutlined />,
      content: this.renderAdoptionReturnForm(),
      onOk: () => {
        return this.adoptionReturnForm.validateFields().then((values) => {
          return adoptionApplicationReturn({
            application_uuid: uuid,
            reason_type_key: values.reason_type,
            description: values.description,
          })
            .then(() => {
              message.success("Application adoption returned successfully!");
            })
            .catch((error) => {
              message.error(
                `Could not return the application adoption. Reason: ${error}`,
                7
              );
            });
        });
      },
    });
  };

  handleAdoptionFoceSetAdopted = (record: any) => {
    if (!record?.pet?.branch?.shelter) return;
    const {
      pet: { branch },
    } = record;
    this.setState({
      currentShelterUuid: branch.shelter.uuid,
      currentBranchUuid: branch.uuid,
      currentApplicationUuid: record.uuid,
      currentApplicationStatus: record.status_key,
    });
  };

  handleSetForceAdoptedClose = (saved?: boolean) => {
    this.setState(
      {
        currentShelterUuid: "",
        currentBranchUuid: "",
        currentApplicationUuid: "",
        status_key: "",
      },
      () => {
        if (saved) {
          this.props.loadAdoptionApplications(this.searchOptions);
        }
      }
    );
  };

  renderAdoptionReturnForm = () => {
    const reasonTypeOptions = [
      {
        label: "SURRENDER",
        value: "SURRENDER",
      },
      {
        label: "LOST",
        value: "LOST",
      },
      {
        label: "RETURN",
        value: "RETURN",
      },
    ];

    return (
      <Form
        name="adoption_return"
        layout="vertical"
        ref={(ref) => (this.adoptionReturnForm = ref)}
      >
        <Form.Item
          label="Reason"
          name="reason_type"
          rules={[
            {
              required: true,
              message: "Missing a reason",
            },
          ]}
        >
          <Select placeholder="Select a reason" options={reasonTypeOptions} />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Missing a description",
            },
          ]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    );
  };

  renderActions = (record: any) => {
    const { uuid, status_key } = record;

    return (
      <Menu>
        {status_key === "ADOPTED" ? (
          <Menu.Item
            key={`adoption-return-${uuid}`}
            onClick={() => this.handleAdoptionReturn(uuid)}
          >
            Adoption Return
          </Menu.Item>
        ) : null}
        {this.ACCEPTABLE_FORCE_ADOPTED.includes(record.status_key) &&
        status_key !== "ADOPTED" ? (
          <Menu.Item
            key={`force-set-adopted-${uuid}`}
            onClick={() => this.handleAdoptionFoceSetAdopted(record)}
          >
            Force Set Adopted
          </Menu.Item>
        ) : null}
      </Menu>
    );
  };

  render() {
    const {
      loading,
      pagination,
      applications,
      integrationContext,
      integrationPartnerContext,
      adoptionApplicationForceSetAdopted,
      loadAdoptionApplications,
    } = this.props;
    const {
      currentShelterUuid,
      currentBranchUuid,
      currentApplicationUuid,
      currentApplicationStatus,
    } = this.state;

   
    return (
      <>
        <Table
          loading={loading}
          rowKey="uuid"
          columns={this.tableColumns}
          pagination={false}
          dataSource={applications}
          onChange={(pagination: any, filters: any, sorter: any) => {
            this.handleTableChange(
              pagination,
              sorter,
              loadAdoptionApplications
            );
          }}
        />
        <Pagination
         loading={loading}
          current={pagination?.page}
          pageSize={pagination?.limit}
          length={applications?.length}
          onChange={(pagination) =>
            this.handleTableChange(
              pagination,
              undefined,
              loadAdoptionApplications
            )
          }
        />
        {currentShelterUuid?.length > 0 && currentApplicationUuid.length > 0 ? (
          <SetForceAdopted
            applicationUuid={currentApplicationUuid}
            status={currentApplicationStatus}
            shelterUuid={currentShelterUuid}
            branchUuid={currentBranchUuid}
            integrationContext={integrationContext}
            integrationPartnerContext={integrationPartnerContext}
            adoptionApplicationForceSetAdopted={
              adoptionApplicationForceSetAdopted
            }
            onClose={this.handleSetForceAdoptedClose}
          />
        ) : null}
      </>
    );
  }
}
